import { DeciderPerformDecision } from '@rabbit/bizproc/core';
import {
  Input,
  Button,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import { FormikProps, Formik, Form } from 'formik';
import { Dispatch, SetStateAction, useState } from 'react';
import * as Yup from 'yup';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import { getRootPersonaFromLexicon } from '@rabbit/bizproc/client';
import { useTranslation } from 'react-i18next';
import {
  PersonaTypeSingleLetter,
  PrincipalsFieldName,
} from '@rabbit/data/types';
import { nestApiGetApprovedWarrantyOffers } from '@rabbit/bizproc/core';

export interface CreateNewSealingProps {
  handleClose: () => void;
  onChange: any; //TODO
  data: any;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}
interface ConsumerDataShape {
  type: string;
  area: string;
  sealing: boolean;
  sealerUsed: string;
}
const validationSchema = Yup.object().shape({
  type: Yup.string().trim().required('Select type of stone'),
  area: Yup.number()
    .nullable()
    .transform((value) => (isNaN(value) ? null : value))
    .positive('Enter a valid number')
    .required('Enter a valid number'),
});
export default function CreateNewSealing(props: CreateNewSealingProps) {
  const { onChange, handleClose, data } = props;
  const { t } = useTranslation();
  const { warrantyDealerPersona, warrantorPersona } = useGetMySagePersonas();

  const activeTenantId =
    warrantyDealerPersona?.warrantyDealer_private?.tenantLink ||
    warrantorPersona?.warrantor_private?.tenantLink;
  const [loading, setLoading] = useState(false);
  const tenantId = activeTenantId;

  const onSubmit = async (
    values: ConsumerDataShape,
    { setFieldError }: FormikProps<ConsumerDataShape>
  ) => {
    const { type, area, sealing } = values;

    // if (!tenantId) {
    //   console.error('No tenantId available');
    //   setFieldError(
    //     'type',
    //     'No tenant ID found. Please check your permissions.'
    //   );
    //   return;
    // }

    const warrantorLink = getRootPersonaFromLexicon(
      t(PrincipalsFieldName),
      PersonaTypeSingleLetter.Warrantor
    );

    setLoading(true);

    try {
      const response = await nestApiGetApprovedWarrantyOffers({
        params: {
          surfaceAreaClass:
            Number(area) >= 15
              ? { label: '>= 15m2', value: 'B' }
              : { label: '< 15m2', value: 'A' },
          surfaceAreaValue: Number(area),
          upsellSealing: true,
        },
        dimensions: ['duration', 'surfaceAreaClass'],
        partnerTenantLink: tenantId,
        warrantorLink: warrantorLink,
      });

      const offers = (response as any)?.offers || [];
      if (!offers || !Array.isArray(offers)) {
        console.error('No offers found in response');
        setLoading(false);
        return;
      }

      const offer = offers.find(
        (item: any) => item.title.toLowerCase() === 'sealing'
      );

      if (!offer) {
        console.error('Sealing offer not found in available offers');
        setFieldError(
          'type',
          'Sealing warranty offer not available for this configuration.'
        );
        setLoading(false);
        return;
      }

      const template = offer.template;
      if (!template) {
        console.error('No template found in offer:', offer);
        setFieldError(
          'type',
          'Selected offer is missing template configuration.'
        );
        setLoading(false);
        return;
      }

      const surfaceAreaClass =
        Number(area) >= 15
          ? { label: '>= 15m2', value: 'B' }
          : { label: '< 15m2', value: 'A' };

      const stipulated = {
        duration: {
          label: '3 years',
          value: 'P3Y',
        },
        surfaceAreaValue: Number(area) || 0,
        surfaceAreaClass,
        upsellSealing: true,
      };

      const decider = DeciderPerformDecision(stipulated, template, offer);

      onChange(2, {
        ...(data || {}),
        surfaceArea: Number(area),
        typeOfStone: type,
        sealing: true,
        sealingOnly: true,
        warranty_duration: 'P3Y',
        warranty_type: template.docid,
        warranty_template: template,
        warranty_price: decider.decided.totalPrice as string,
        warranty_offer: offer,
        decider: decider,
        sealerUsed: values.sealerUsed,
        warranty_start_date: new Date().toDateString(),
      });
    } catch (error) {
      console.error('Error in CreateNewSealing:', error);
      setFieldError(
        'type',
        'Failed to fetch warranty offers. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const stoneTypeOptions = [
    { id: '0', label: 'Marble', value: 'marble' },
    { id: '1', label: 'Granite', value: 'granite' },
    { id: '2', label: 'Engineered Stone', value: 'engineered-stone' },
    { id: '3', label: 'Limestone', value: 'limestone' },
    { id: '4', label: 'Quartzite', value: 'quartzite' },
    { id: '5', label: 'Porcelain', value: 'porcelain' },
    { id: '6', label: 'Travertine', value: 'travertine' },
    { id: '7', label: 'Multi-Stone', value: 'multi-stone' },
  ].filter((i) => i.value !== 'porcelain' && i.value !== 'engineered-stone');

  return (
    <>
      {loading && <LoadingSpinner size="sm" overlay={true} />}
      <Formik
        initialValues={{
          type: '',
          area: '',
          sealing: true,
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit as any}
        validateOnChange={true}
        validateOnBlur={false}
      >
        {(props) => (
          <Form className="flex-col gap-3 p-5">
            <div className="mb-4 flex gap-4">
              <Input
                type="select"
                label="Type of stone*"
                name="type"
                settings={{
                  id: 'type',
                  placeholder: 'Please select an option',
                  options: stoneTypeOptions,
                }}
              />
              <Input
                type="text"
                label="Area*"
                name="area"
                settings={{
                  id: 'area',
                  placeholder: 'Please enter area',
                  allowSpecialCharacter: true,
                  append: 'm²',
                }}
              />
            </div>
            <Input
              type="rich-text"
              label="Sealer used"
              name="sealerUsed"
              className="mb-4"
              settings={{
                id: 'sealerUsed',
                placeholder: 'Please enter sealer',
              }}
            />
            <div className="mt-5 flex w-full gap-4">
              <Button
                kind="primary"
                type="submit"
                disabled={Object.keys(props.errors).length > 0}
              >
                Continue
              </Button>
              <Button kind="red" type="button" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
